<template>
  <!-- ======================= -->
  <!-- TAB: ARMOUR             -->
  <!-- ======================= -->
  <div>
    <!-- ----------------- -->
    <!--   ARMOUR DETAILS  -->
    <!-- ----------------- -->
    <div class="row mt-2"></div>
    <p class="bb-hdg mt-2 mb-3">Armour Details</p>
    <!-- -------------- -->
    <!--      ROW 1     -->
    <!-- -------------- -->
    <div class="row">
      <!-- ----------------------------------- -->
      <!--   equipped (worn) armour (switch)  -->
      <!-- ----------------------------------- -->
      <div
        v-if="showWornSwitch"
        class="col-auto pe-3"
      >
        <enhanced-toggle
          style="
            --toggleWidth: 260px;
            --labelWidth: 160px;
            --switchWidth: 90px;
            --switchPos: 160px;
            height: 50px;
            border-radius: 10px;
            background-color: white;
          "
          :styleOff="`default`"
          :styleOn="`primary`"
          :labelOn="`Worn`"
          :labelOff="`Not Worn`"
          :checked="activeItem.equipped"
          @update:modelValue="onEquippedToggle"
        />
      </div>
    </div>
    <!-- ---------- -->
    <!--    ROW 2   -->
    <!-- ---------- -->
    <div class="row mt-2">
      <!-- ------------------------------------ -->
      <!--   armour locations (coverage) (r/o)  -->
      <!-- ------------------------------------ -->
      <div class="col form-group">
        <div class="form-floating">
          <input
            readonly
            class="form-control text-left"
            :value="activeItem.coverage"
            id="coverage"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="coverage"
            >Armour Locations</label
          >
        </div>
      </div>
      <!-- --------------------- -->
      <!--   armour state (r/o)  -->
      <!-- --------------------- -->
      <div
        v-if="showStateField"
        class="col-5 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            :class="[
              'form-control',
              'text-center',
              activeItem.hp === 0 ? 'bb-item-warning' : '',
            ]"
            :value="computeItemState"
            id="state"
            disabled
          />
          <label
            :class="[
              'bb-float-label',
              'optional',
              activeItem.hp === 0 ? 'bb-item-warning-label' : '',
            ]"
            for="state"
          >
            State
          </label>
        </div>
      </div>
    </div>
    <!-- ---------- -->
    <!--    ROW 3   -->
    <!-- ---------- -->
    <div
      v-if="activeItem.equipped && armourLocations.length > 0"
      class="row mt-2"
    >
      <div class="col form-group">
        <div class="form-floating">
          <v-select
            :options="armourLocations"
            label="name"
            :reduce="reduceObject"
            class="form-control"
            placeholder="select..."
            :model-value="localItem.armour_location"
            @keydown="onKeyDown($event)"
            :class="{ 'is-invalid': errorFields.includes('armour_location') }"
            @update:modelValue="onArmourLocationSelect"
            id="armour_location"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label"
            for="armour_location"
            >Specific Armour Location</label
          >
        </div>
      </div>
    </div>
    <!-- ---------- -->
    <!--    ROW 4   -->
    <!-- ---------- -->
    <div class="row mt-2 mb-2">
      <!-- --------- -->
      <!--    layer  -->
      <!-- --------- -->
      <div class="col-3 form-group">
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="activeItem.layer"
            id="layer"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="layer"
            >Layer</label
          >
        </div>
      </div>
      <!-- ------------ -->
      <!--    dex pen   -->
      <!-- ------------ -->
      <div class="col-3 form-group">
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="dexPenDisplay"
            id="dex_pen"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="dex_pen"
            >Dex Pen %</label
          >
        </div>
      </div>
      <div
        v-if="showPwrStorage"
        class="col-5 form-group"
      >
        <CounterEmit
          ref="pwrStorageCounter"
          :pass_ref="'pwr_storage'"
          :pass_label="'Pwr Str'"
          :pass_count="localItem.pwr_storage ?? activeItem.pwr_storage ?? 0"
          :pass_min="0"
          :pass_max="20"
          :pass_digits="2"
          @change="onCounterChanged"
        />
      </div>
      <!-- ---------- -->
      <!--    ROW 5   -->
      <!-- ---------- -->
      <div class="row mt-2 mb-2">
        <!-- -------- -->
        <!--    AP    -->
        <!-- -------- -->
        <div class="col-5 form-group">
          <CounterEmit
            ref="apCounter"
            :pass_ref="'ap'"
            :pass_label="'AP'"
            :pass_count="localItem.ap"
            :pass_min="0"
            :pass_max="maxAp"
            :pass_digits="2"
            @change="onCounterChanged"
          />
        </div>
        <!-- -------- -->
        <!--    HP    -->
        <!-- -------- -->
        <div class="col-5 form-group">
          <CounterEmit
            ref="hpCounter"
            :pass_ref="'hp'"
            :pass_label="'HP'"
            :pass_count="localItem.hp"
            :pass_min="0"
            :pass_max="maxHp"
            :pass_digits="2"
            @change="onCounterChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CounterEmit from './CounterEmit.vue'

export default {
  name: 'ArtefactArmourTab',
  components: {
    CounterEmit,
  },
  props: {
    activeItem: {
      type: Object,
      required: true,
    },
    localItem: {
      type: Object,
      required: true,
    },
    armourLocations: {
      type: Array,
      required: true,
    },
    errorFields: {
      type: Array,
      required: true,
    },
    maxAp: {
      type: Number,
      required: true,
    },
    maxHp: {
      type: Number,
      required: true,
    },
    computeItemState: {
      type: String,
      default: '',
    },
    showStateField: {
      type: Boolean,
      default: true,
    },
    dexPenDisplay: {
      type: Number,
      required: true,
    },
    reduceObject: {
      type: Function,
      required: true,
    },
    onKeyDown: {
      type: Function,
      required: true,
    },
    onArmourLocationSelect: {
      type: Function,
      required: true,
    },
    onEquippedToggle: {
      type: Function,
      required: true,
    },
    onCounterChanged: {
      type: Function,
      required: true,
    },
    onArtefactFieldChange: {
      type: Function,
      required: true,
    },
    showWornSwitch: {
      type: Boolean,
      default: true,
    },
    showPwrStorage: {
      type: Boolean,
      default: true,
    },
  },
}
</script>
